'use client';

import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useCallback
} from 'react';
import { useAuth } from './auth-context';

export type UserRole = 'ADMIN' | 'SUPER_ADMIN' | 'USER';

type RBACContextType = {
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  userRole: UserRole | null;
  isLoading: boolean;
};

const RBACContext = createContext<RBACContextType | undefined>(undefined);

export function RBACProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();

  const userRole: UserRole | null = useMemo(() => {
    return typeof user?.role === 'string' ? (user.role as UserRole) : null;
  }, [user?.role]);

  const isLoading = authLoading;

  const hasRole = useCallback(
    (roles: UserRole | UserRole[]): boolean => {
      if (isLoading) return false;
      if (!userRole) return false;
      return Array.isArray(roles)
        ? roles.includes(userRole)
        : roles === userRole;
    },
    [isLoading, userRole]
  );

  return (
    <RBACContext.Provider
      value={{
        hasRole,
        userRole,
        isLoading
      }}
    >
      {children}
    </RBACContext.Provider>
  );
}

export function useRoleGate() {
  const context = useContext(RBACContext);

  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }

  return context;
}
